import { supabase } from "@/integrations/supabase/client";

/** Referral claim timeline (user-facing) and admin fraud-hold review queue. */

export type ReferralTimelineRow = {
  referral_id: string;
  status: string;
  review_status: string;
  risk_flags: string[] | null;
  hold_reason: string | null;
  reasons: unknown;
  reward_credits: number | null;
  validated_amount_cents: number | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
};

export type ReferralReviewRow = {
  referral_id: string;
  referrer_id: string;
  referred_user_id: string;
  code: string;
  status: string;
  review_status: string;
  risk_score: number | null;
  risk_flags: string[] | null;
  hold_reason: string | null;
  validated_amount_cents: number | null;
  created_at: string;
  signals: { signal: string; severity: number; detail?: Record<string, unknown> }[] | null;
  review_log: { decision?: string; note?: string; at?: string; actor?: string }[] | null;
};

/** Human explanations for each fraud signal, shown to users and admins. */
export const FRAUD_REASONS: Record<string, string> = {
  same_canonical_email_as_referrer:
    "The invited address looks like an alias of the referrer's own email.",
  disposable_email: "The invited account used a disposable email domain.",
  device_reused_across_referrals:
    "The same device signature already appeared on another referral.",
  device_matches_referrer_network: "The signup device matches the referrer's own device.",
  ip_reused_across_referrals: "Several referrals were created from the same network.",
  referrer_velocity_spike: "This account created an unusual number of referrals in 24 hours.",
};

export function explainFlags(flags: string[] | null | undefined): string[] {
  return (flags ?? []).map((f) => FRAUD_REASONS[f] ?? f.replace(/_/g, " "));
}

export const STATUS_LABELS: Record<string, string> = {
  pending: "Pending purchase",
  completed: "Approved & paid out",
  held_for_review: "Held for review",
  void: "Voided",
};

export async function fetchReferralTimeline(): Promise<ReferralTimelineRow[]> {
  const { data, error } = await supabase.rpc("referral_timeline_for_user");
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ReferralTimelineRow[];
}

export async function fetchReferralReviewQueue(
  status: "review" | "blocked" | "clear" | "all" = "review",
): Promise<ReferralReviewRow[]> {
  const { data, error } = await supabase.rpc(
    "admin_referral_review_queue",
    status === "all" ? {} : { _status: status },
  );
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ReferralReviewRow[];
}

/** Approves (releases credits) or voids a held referral, logging the decision. */
export async function decideReferral(opts: {
  referralId: string;
  decision: "approve" | "void";
  note: string;
}): Promise<void> {
  if (opts.decision === "approve") {
    const { error } = await supabase.rpc("admin_release_referral", {
      _referral_id: opts.referralId,
      _note: opts.note,
    });
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await supabase.rpc("admin_override_referral", {
    _referral_id: opts.referralId,
    _decision: "void",
    _note: opts.note,
  });
  if (error) throw new Error(error.message);
}
